import { useQueryClient } from '@tanstack/react-query'
import { ChevronLeft, ChevronRight, Ellipsis, FolderPlus, Info, PenLine, X } from 'lucide-react'
import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react'
import { Link } from 'react-router'
import { showDate } from '../../../shared/tree'
import { CropPhoto } from '@/components/CropPhoto'
import { Loader } from '@/components/Loader'
import { PhotoSheet } from './PhotoSheet'
import { OriginalPhoto } from '@/components/OriginalPhoto'
import { SendPhotos } from '@/components/SendTo'
import { Avatar } from '@/components/tree/Avatar'
import { afterPhotoEdit, albumPhotoUrl, photoName, photoQuery, photoSrcSet, usePhoto, type Photo } from '@/lib/albums'
import { addedAgo, relatedQuery, tileRatio, useRelated, type Show, type WallPhoto } from '@/lib/modern'
import { saveCrop } from '@/lib/photos'
import { glowOf, reducedMotion, rubber, velocity } from '@/lib/motion'
import { useIsPhone } from '@/lib/platform'
import { tint } from '@/lib/paper'
import { cn } from '@/lib/utils'

const round =
  'grid size-11 shrink-0 place-items-center rounded-full outline-none transition-[background-color,scale] active:scale-90 focus-visible:ring-[3px] focus-visible:ring-ring/60'
// Over the photo: frosted rounds that read over any picture.
const floating = 'pointer-events-auto bg-viewer/45 text-viewer-foreground backdrop-blur-md hover:bg-viewer/65'
const chip =
  'inline-flex min-h-9 shrink-0 items-center gap-2 rounded-full bg-secondary px-3.5 text-sm font-medium outline-none transition-[background-color,scale] hover:bg-accent active:scale-95 focus-visible:ring-[3px] focus-visible:ring-ring/50'
const action =
  'inline-flex h-10 items-center gap-2 rounded-full bg-secondary px-4 text-sm font-medium outline-none transition-[background-color,scale] hover:bg-accent active:scale-95 focus-visible:ring-[3px] focus-visible:ring-ring/50'

// A photo opened from the wall, as a post: who added it and where it's from, the photo whole, what can be done with
// it, its words and tags, and then more to look at from the same day. On a phone it's a page of its own, the photo
// first across the whole width with the way back on it: swipe the photo sideways for the next one on the wall, pull it
// down to put it back. On a tablet or computer the photo sits in
// a dark room lit by its own colours, with the post beside it, and the arrow keys step through the wall.
export function PhotoPost({
  id,
  known,
  list,
  familyId,
  moments,
  onClose,
  onGo,
  onSwap,
  onShow,
}: {
  id: string
  // What the wall already knows about it, to draw it at once while the rest arrives.
  known?: WallPhoto
  list: WallPhoto[]
  familyId: string
  // Whether Moments is on, for "Write a memory".
  moments: boolean
  onClose: () => void
  // Swiped or stepped to another photo on the wall; it slides in from that side.
  onGo: (id: string, direction: 1 | -1) => void
  // A photo from the same day picked: it flies up into the photo's place.
  onSwap: (id: string, from: HTMLElement) => void
  onShow: (show: Show) => void
}) {
  const phone = useIsPhone()
  const sizes = phone ? '100vw' : 'calc(100vw - 26rem)'
  const photo = usePhoto(id)
  const data = photo.data?.id === id ? photo.data : undefined
  const shape = known ?? data
  const related = useRelated(id, familyId)
  const root = useRef<HTMLDivElement>(null)
  const sheet = useRef<HTMLDivElement>(null)
  const hero = useRef<HTMLDivElement>(null)
  const [sending, setSending] = useState(false)
  const [original, setOriginal] = useState(false)
  const [options, setOptions] = useState(false)
  const [editing, setEditing] = useState(false)
  const queryClient = useQueryClient()
  const at = list.findIndex((p) => p.id === id)
  const before = at > 0 ? list[at - 1] : undefined
  const after = at >= 0 ? list[at + 1] : undefined
  // The photo's own colour, once its picture has loaded (or from the wall, which already knew it).
  const [lit, setLit] = useState<{ id: string; glow: string | null }>()
  const glow = lit?.id === id ? lit.glow : glowOf(id, null)
  // The side the photo came in from, to slide it in from there.
  const arriving = useRef<1 | -1 | 0>(0)

  // Locked behind it: the page doesn't scroll, and focus starts on the post itself, so a screen reader reads it and Tab
  // goes next to the way out. (Focusing the button instead drew its focus ring on every photo opened on an iPhone.)
  useLayoutEffect(() => {
    const html = document.documentElement
    const was = html.style.overflow
    html.style.overflow = 'hidden'
    root.current?.focus({ preventScroll: true })
    return () => {
      html.style.overflow = was
    }
  }, [])

  const go = (next: string, direction: 1 | -1) => {
    const el = hero.current
    if (!el || reducedMotion()) return onGo(next, direction)
    el.animate(
      [{ transform: el.style.transform || 'none' }, { transform: `translateX(${-direction * 110}%)`, opacity: 0.3 }],
      {
        duration: 200,
        easing: 'cubic-bezier(0.4, 0, 1, 1)',
        fill: 'forwards',
      },
    ).onfinish = () => {
      el.style.transform = ''
      arriving.current = direction
      onGo(next, direction)
    }
  }

  useEffect(() => {
    const keys = (e: KeyboardEvent) => {
      if (
        sending ||
        options ||
        original ||
        (e.target as HTMLElement).closest('input, textarea, [role="dialog"] [role="dialog"]')
      )
        return
      if (e.key === 'Escape') onClose()
      else if (e.key === 'ArrowRight' && after) go(after.id, 1)
      else if (e.key === 'ArrowLeft' && before) go(before.id, -1)
    }
    addEventListener('keydown', keys)
    return () => removeEventListener('keydown', keys)
  })

  // The photos either side, fetched ahead with what's said about them, so swiping to one shows it and its words at once
  // rather than the picture first and the rest after.
  useEffect(() => {
    for (const next of [after, before]) {
      if (!next) continue
      void queryClient.prefetchQuery(photoQuery(next.id))
      void queryClient.prefetchQuery(relatedQuery(next.id, familyId))
      const img = new Image()
      img.sizes = sizes
      img.srcset = photoSrcSet(next, ['medium', 'display'])
      img.src = albumPhotoUrl(next.ref, 'medium')
    }
  }, [id]) // eslint-disable-line react-hooks/exhaustive-deps

  // A new photo: back to the top, and in from the side it was swiped from.
  useLayoutEffect(() => {
    root.current?.scrollTo({ top: 0 })
    const panel = root.current?.querySelector('.mp-panel')
    panel?.scrollTo({ top: 0 })
    const el = hero.current
    const from = arriving.current
    arriving.current = 0
    // The way out of the last photo held it off to the side; let go of that.
    for (const a of el?.getAnimations() ?? []) a.cancel()
    if (el && from && !reducedMotion())
      el.animate(
        [
          { transform: `translateX(${from * 40}%) scale(0.94)`, opacity: 0 },
          { transform: 'none', opacity: 1 },
        ],
        { duration: 420, easing: 'cubic-bezier(0.2, 0.8, 0.2, 1)' },
      )
  }, [id])

  // A finger on the photo (on a phone): sideways steps through the wall, down from the top puts it back. Anything else
  // is the page scrolling, as usual.
  useEffect(() => {
    const surface = sheet.current
    const el = hero.current
    if (!phone || !surface || !el) return
    let start: { x: number; y: number } | null = null
    let axis: 'x' | 'down' | 'scroll' | null = null
    let points: { t: number; x: number; y: number }[] = []
    const down = (e: TouchEvent) => {
      if (e.touches.length !== 1) return
      const t = e.touches[0]!
      start = { x: t.clientX, y: t.clientY }
      axis = null
      points = []
    }
    const move = (e: TouchEvent) => {
      if (!start) return
      const t = e.touches[0]!
      const dx = t.clientX - start.x
      const dy = t.clientY - start.y
      points.push({ t: performance.now(), x: t.clientX, y: t.clientY })
      if (!axis && Math.hypot(dx, dy) > 8) {
        const onPhoto = el.contains(e.target as Node)
        if (onPhoto && Math.abs(dx) > Math.abs(dy)) axis = 'x'
        else if (dy > 0 && (root.current?.scrollTop ?? 0) <= 0) axis = 'down'
        else axis = 'scroll'
      }
      if (axis === 'x') {
        e.preventDefault()
        const edge = (dx > 0 && !before) || (dx < 0 && !after)
        el.style.transform = `translateX(${edge ? rubber(dx, 80) : dx}px)`
      } else if (axis === 'down') {
        e.preventDefault()
        const pull = Math.max(0, dy)
        surface.style.transform = `translateY(${pull}px) scale(${1 - Math.min(pull, 500) / 1800})`
        surface.style.borderRadius = `${Math.min(pull / 4, 32)}px`
        root.current!.style.setProperty('--fade', String(Math.max(0, 1 - pull / 420)))
      }
    }
    const up = () => {
      if (!start) return
      const v = velocity(points)
      const last = points.at(-1)
      const dx = last ? last.x - start.x : 0
      const dy = last ? last.y - start.y : 0
      start = null
      if (axis === 'x') {
        const next = dx < 0 ? after : before
        if (next && (Math.abs(dx) > el.clientWidth * 0.22 || Math.abs(v.x) > 0.45)) return go(next.id, dx < 0 ? 1 : -1)
        el.animate([{ transform: el.style.transform }, { transform: 'none' }], {
          duration: 380,
          easing: 'cubic-bezier(0.34, 1.36, 0.64, 1)',
        })
        el.style.transform = ''
      } else if (axis === 'down') {
        if (dy > 110 || v.y > 0.5) return onClose()
        surface.animate([{ transform: surface.style.transform }, { transform: 'none' }], {
          duration: 380,
          easing: 'cubic-bezier(0.34, 1.36, 0.64, 1)',
        })
        surface.style.transform = ''
        surface.style.borderRadius = ''
        root.current!.style.removeProperty('--fade')
      }
    }
    surface.addEventListener('touchstart', down, { passive: true })
    surface.addEventListener('touchmove', move, { passive: false })
    surface.addEventListener('touchend', up)
    surface.addEventListener('touchcancel', up)
    return () => {
      surface.removeEventListener('touchstart', down)
      surface.removeEventListener('touchmove', move)
      surface.removeEventListener('touchend', up)
      surface.removeEventListener('touchcancel', up)
    }
  })

  const name = shape
    ? photoName({ caption: data?.caption ?? known?.caption ?? '', takenOn: shape.takenOn }, 1)
    : 'A photo'
  const title = data?.title || name
  const ratio = shape ? shape.width / Math.max(shape.height, 1) : 1
  const picture = shape && (
    <div
      key={id}
      ref={hero}
      className={cn('mp-hero', tint(at < 0 ? 0 : at))}
      style={{
        aspectRatio: String(ratio),
        width: phone ? 'calc(100vw - 0.5rem)' : `min(100%, calc((100svh - 7rem) * ${ratio}))`,
      }}
    >
      <img src={albumPhotoUrl((known ?? data)!.ref, 'thumb')} alt="" aria-hidden draggable={false} />
      <img
        className="mp-sharp"
        src={albumPhotoUrl((known ?? data)!.ref, 'medium')}
        srcSet={photoSrcSet(known ?? data!, ['medium', 'display'])}
        sizes={sizes}
        alt={title}
        draggable={false}
        ref={(img) => {
          if (img?.complete && img.naturalWidth) img.dataset.loaded = 'true'
        }}
        onLoad={(e) => {
          e.currentTarget.dataset.loaded = 'true'
          setLit({ id, glow: glowOf(id, e.currentTarget) })
        }}
      />
    </div>
  )

  // Everything said about it shows at once, when it's all here: who added it, its words and tags, and the photos from
  // the same day. If that takes a moment, the circles say so (Loader waits before it shows, so a quick one never does).
  const ready = !!data && !related.isPending
  const optionsButton = data && (
    <button
      type="button"
      onClick={() => setOptions(true)}
      className={cn(round, 'ml-auto hover:bg-accent')}
      aria-label="Photo options"
    >
      <Ellipsis className="size-6" aria-hidden />
    </button>
  )
  const details = (
    <Details
      photo={data}
      familyId={familyId}
      moments={moments}
      onShow={onShow}
      onSend={() => setSending(true)}
      related={related.data}
      onSwap={onSwap}
      phone={phone}
    />
  )
  const about = (pad: string) =>
    ready ? (
      <div key={id} className={cn('mp-appear flex flex-col', phone ? 'gap-4' : 'gap-5')}>
        <Byline photo={data} className={pad}>
          {optionsButton}
        </Byline>
        {details}
      </div>
    ) : (
      <div className="grid min-h-40 place-items-center">
        <Loader label="Loading the photo’s details" />
      </div>
    )
  const style = (glow ? { '--glow': glow } : {}) as CSSProperties
  return (
    <div
      ref={root}
      className="mp-post outline-none"
      role="dialog"
      aria-modal="true"
      aria-label={title}
      tabIndex={-1}
      style={phone ? { ...style, background: 'transparent' } : style}
    >
      {phone ? (
        <>
          <div
            aria-hidden
            className="fixed inset-0 bg-background"
            style={{ opacity: 'var(--fade, 1)' } as CSSProperties}
          />
          <div ref={sheet} className="relative min-h-full overflow-hidden bg-background">
            <div className="mp-post-glow" />
            {/* The photo across the whole width right under the clock, as Pinterest's are, the way back floating on its
                top corner and its options under it. */}
            <div className="relative flex flex-col gap-4 pt-[calc(var(--safe-top)+0.25rem)] pb-[calc(var(--safe-bottom)+2rem)]">
              <div className="relative">
                {picture}
                <div className="pointer-events-none absolute inset-x-1 top-0 flex items-center p-2.5">
                  <button type="button" onClick={onClose} className={cn(round, floating)} aria-label="Back to photos">
                    <ChevronLeft className="size-7" aria-hidden />
                  </button>
                </div>
              </div>
              {about('px-4')}
            </div>
          </div>
        </>
      ) : (
        <>
          <div
            className="mp-stage"
            onClick={(e) => {
              if (e.target === e.currentTarget) onClose()
            }}
          >
            <button
              type="button"
              onClick={onClose}
              className={cn(round, 'absolute top-4 left-4 text-viewer-foreground hover:bg-viewer-foreground/10')}
              aria-label="Close"
            >
              <X className="size-6" aria-hidden />
            </button>
            {before && (
              <button
                type="button"
                onClick={() => go(before.id, -1)}
                className={cn(
                  round,
                  'absolute top-1/2 left-4 -translate-y-1/2 text-viewer-muted hover:bg-viewer-foreground/10 hover:text-viewer-foreground',
                )}
                aria-label="Previous photo"
              >
                <ChevronLeft className="size-8" aria-hidden />
              </button>
            )}
            {after && (
              <button
                type="button"
                onClick={() => go(after.id, 1)}
                className={cn(
                  round,
                  'absolute top-1/2 right-4 -translate-y-1/2 text-viewer-muted hover:bg-viewer-foreground/10 hover:text-viewer-foreground',
                )}
                aria-label="Next photo"
              >
                <ChevronRight className="size-8" aria-hidden />
              </button>
            )}
            {picture}
          </div>
          <aside className="mp-panel py-6">{about('px-6')}</aside>
        </>
      )}
      {data && (
        <PhotoSheet
          photo={data}
          name={name}
          open={options}
          onOpenChange={setOptions}
          onDownload={() => {
            setOptions(false)
            setOriginal(true)
          }}
          onEdit={() => {
            setOptions(false)
            setEditing(true)
          }}
        />
      )}
      {data?.canEdit && (
        <CropPhoto
          open={editing}
          onOpenChange={setEditing}
          source={albumPhotoUrl(data.id, 'original')}
          crop={data.crop}
          medium
          onSave={async (crop, copies) => {
            await saveCrop(`/album-photos/${data.id}`, crop, copies)
            await afterPhotoEdit(queryClient)
          }}
        />
      )}
      {data && (
        <OriginalPhoto
          open={original}
          onOpenChange={setOriginal}
          src={albumPhotoUrl(data.id, 'original')}
          fallback={albumPhotoUrl(data.ref, 'display')}
          alt={title}
        />
      )}
      {data && (
        <SendPhotos
          open={sending}
          onOpenChange={setSending}
          familyId={familyId}
          photos={[data.id]}
          inAlbum={!!data.album}
          onSent={() => setSending(false)}
          onNewAlbum={() => {
            setSending(false)
            location.assign(`/ph/${data.id}`)
          }}
        />
      )}
    </div>
  )
}

// Who added it, when, and where it lives.
function Byline({ photo, className, children }: { photo?: Photo; className?: string; children?: ReactNode }) {
  return (
    <div className={cn('flex min-h-11 items-center gap-3', className)}>
      {photo && (
        <>
          <span className="grid size-10 shrink-0 place-items-center rounded-full bg-secondary font-semibold">
            {photo.by.trim().charAt(0).toUpperCase()}
          </span>
          <span className="flex min-w-0 flex-col leading-tight">
            <span className="truncate font-semibold">{photo.by}</span>
            <span className="truncate text-sub text-muted-foreground">
              {[photo.album?.title, photo.takenOn ? showDate(photo.takenOn) : `Added ${addedAgo(photo.createdAt)}`]
                .filter(Boolean)
                .join(' · ')}
            </span>
          </span>
        </>
      )}
      {children}
    </div>
  )
}

function Details({
  photo,
  familyId,
  moments,
  onShow,
  onSend,
  related,
  onSwap,
  phone,
}: {
  photo?: Photo
  familyId: string
  moments: boolean
  onShow: (show: Show) => void
  onSend: () => void
  related?: { title: string; photos: WallPhoto[] }
  onSwap: (id: string, from: HTMLElement) => void
  phone: boolean
}) {
  const pad = phone ? 'px-4' : 'px-6'
  const year = photo?.takenOn?.slice(0, 4)
  return (
    <>
      {photo && (
        <div className={cn('flex flex-col gap-4', pad)}>
          <div className="flex flex-wrap gap-2">
            {moments && (
              <Link
                to={`/f/${familyId}/moments/new${photo.people[0] ? `?with=${encodeURIComponent(photo.people[0].id)}` : ''}`}
                className={action}
              >
                <PenLine className="size-4" aria-hidden /> Write a memory
              </Link>
            )}
            {photo.canEdit && (
              <button type="button" onClick={onSend} className={action}>
                <FolderPlus className="size-4" aria-hidden /> {photo.album ? 'Move to album' : 'Add to album'}
              </button>
            )}
            {!phone && (
              <Link to={`/ph/${photo.id}`} className={action}>
                <Info className="size-4" aria-hidden /> Details
              </Link>
            )}
          </div>
          {photo.title && <h2 className="text-xl font-semibold tracking-tight text-balance">{photo.title}</h2>}
          {photo.caption && <p className="text-lg leading-snug text-pretty">{photo.caption}</p>}
          {(photo.people.length > 0 || year || photo.album || photo.tags.length > 0) && (
            <div className="flex flex-wrap gap-2" aria-label="Tags">
              {photo.people.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  className={cn(chip, 'pl-1.5')}
                  onClick={() => onShow({ kind: 'person', id: p.id })}
                >
                  <Avatar person={p} className="size-6.5 text-xs shadow-none" />
                  {p.name}
                </button>
              ))}
              {year && /^\d{4}$/.test(year) && (
                <button type="button" className={chip} onClick={() => onShow({ kind: 'year', year })}>
                  {year}
                </button>
              )}
              {photo.album && (
                <button type="button" className={chip} onClick={() => onShow({ kind: 'album', id: photo.album!.id })}>
                  {photo.album.title}
                </button>
              )}
              {photo.tags.map((t) => (
                <button key={t} type="button" className={chip} onClick={() => onShow({ kind: 'tag', tag: t })}>
                  #{t}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
      {related && related.photos.length > 0 && (
        <section className={cn('flex flex-col gap-3 pt-2', pad)} aria-label={related.title}>
          <h2 className="text-lg font-semibold tracking-tight">{related.title}</h2>
          <ul className={cn('gap-2', phone ? 'columns-2' : 'columns-3')}>
            {related.photos.map((p, i) => (
              <li key={p.id} className="mb-2 break-inside-avoid">
                <button
                  type="button"
                  className="group block w-full overflow-hidden rounded-xl outline-none focus-visible:ring-[3px] focus-visible:ring-ring/60"
                  aria-label={photoName(p, i + 1)}
                  onClick={(e) => onSwap(p.id, e.currentTarget.querySelector('img')!)}
                >
                  <img
                    src={albumPhotoUrl(p.ref, 'thumb')}
                    alt=""
                    decoding="async"
                    loading="lazy"
                    className={cn('block w-full transition-transform duration-300 group-active:scale-95', tint(i))}
                    style={{ aspectRatio: String(tileRatio(p)), objectFit: 'cover' }}
                  />
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}
    </>
  )
}
